<template>
  <div>
    <h3 class="abstract font-bold text-black-light text-center mb-4">
      Contattaci
    </h3>
    <p class="text-black-light text-center mb-4">
      Per informazioni o richieste di appuntamento chiama il nostro Numero Verde
      o compila la form.
    </p>
    <p class="text-sm text-black-light mb-2">* Campi obbligatori</p>
    <form role="form">
      <ValidationObserver v-slot="{ errors }" ref="observer" :slim="true">
        <InputWithValidation
          :value="first_name"
          name="name"
          label="Nome*"
          aria-required="true"
          type="text"
          placeholder="Nome"
          rules="required|alpha_spaces"
          :errors="errors"
          :field-classes="errors.name?.length ? 'mb-22' : 'mb-6'"
          @focus="onTealiumFormFirstFieldFocus"
          @input="onFormFieldEvent($event, 'first_name', 'name')"
        />
        <InputWithValidation
          :value="last_name"
          name="surname"
          label="Cognome*"
          aria-required="true"
          type="text"
          placeholder="Cognome"
          rules="required|alpha_spaces"
          :errors="errors"
          :field-classes="errors.surname?.length ? 'mb-22' : 'mb-6'"
          @input="onFormFieldEvent($event, 'last_name', 'surname')"
        />
        <InputWithValidation
          :value="email"
          name="email"
          label="Email*"
          aria-required="true"
          type="email"
          placeholder="Email"
          rules="required|email"
          :errors="errors"
          :field-classes="errors.email?.length ? 'mb-22' : 'mb-6'"
          @input="onFormFieldEvent($event, 'email')"
        />
        <InputWithValidation
          :value="phone"
          name="phone"
          label="Telefono*"
          aria-required="true"
          type="number"
          placeholder="Telefono"
          rules="required|phone"
          :errors="errors"
          :field-classes="errors.phone?.length ? 'mb-22' : 'mb-6'"
          @input="onFormFieldEvent($event, 'phone')"
        />
        <InputWithValidation
          :value="message"
          name="message"
          label="Messaggio"
          placeholder="Messaggio"
          :field-classes="errors.message?.length ? 'mb-22' : 'mb-6'"
          @input="onFormFieldEvent($event, 'message')"
        />
        <Accordion
          :border-top="false"
          :border-bottom="false"
          :padding="false"
          :is-open="hasBuyingInterest === 1"
          tabindex="-1"
        >
          <template #trigger="{ accordionId }">
            <UcrsSwitch
              v-model="hasBuyingInterest"
              class="mb-6"
              name="hasBuyingInterest"
              :true-value="1"
              :false-value="0"
              :left-label="true"
              :aria-controls="`AccordionContent-${accordionId}`"
              :aria-expanded="`${hasBuyingInterest === 1}`"
            >
              <p :id="`Accordion-${accordionId}`">Stai cercando casa?</p>
            </UcrsSwitch>
          </template>
          <div>
            <DropdownWithValidation
              :value="category"
              :options="categories"
              name="category"
              aria-required="true"
              placeholder="Categoria*"
              :rules="hasBuyingInterest === 1 ? 'required' : ''"
              :errors="errors"
              :field-classes="errors.category?.length ? 'mb-22' : 'mb-6'"
              @select="onSelectCategory($event)"
            />
            <DropdownWithValidation
              :value="typology"
              :options="typologies"
              name="typology"
              aria-required="true"
              placeholder="Tipologia*"
              :rules="hasBuyingInterest === 1 ? 'required' : ''"
              :errors="errors"
              :field-classes="errors.typology?.length ? 'mb-22' : 'mb-6'"
              @select="onFormFieldEvent($event, 'typology')"
            />
            <DropdownWithValidation
              :value="subAdministrativeArea"
              :options="subAdministrativeAreas"
              name="state"
              aria-required="true"
              placeholder="Provincia*"
              :rules="hasBuyingInterest === 1 ? 'required' : ''"
              :errors="errors"
              :field-classes="errors.state?.length ? 'mb-22' : 'mb-6'"
              @select="onSelectState($event)"
            />
            <DropdownWithValidation
              :value="city"
              :options="cities"
              name="city"
              aria-required="true"
              placeholder="Comune*"
              :rules="hasBuyingInterest === 1 ? 'required' : ''"
              :errors="errors"
              :field-classes="errors.city?.length ? 'mb-22' : 'mb-6'"
              @select="onFormFieldEvent($event, 'city')"
            />
            <p class="text-sm text-black-light mb-6">* Campi obbligatori</p>
          </div>
        </Accordion>
        <UcrsSwitch
          v-model="hasSellingInterest"
          class="mb-6"
          name="hasSellingInterest"
          :true-value="1"
          :false-value="0"
          :left-label="true"
        >
          Sono interessato a vendere il mio immobile
        </UcrsSwitch>
        <PrivacyCheckbox
          v-if="!$store.state.auth.loggedIn"
          v-model="privacy"
          name="privacy"
          modal-width="123rem"
          :errors="errors"
          :class="errors.privacy?.length ? 'mb-22' : 'mb-6'"
        />
      </ValidationObserver>
      <OButton
        variant="primary"
        root-class="ucrs-button--large w-full"
        :disabled="isDisabled"
        @click.prevent="submit"
      >
        Invia
      </OButton>
    </form>
    <p class="mt-7 text-lg text-center">
      Numero verde
      <a
        class="text-blue font-bold underline outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:rounded focus-visible:ring-blue"
        href="tel:+39800896968"
      >
        800.89.69.68
      </a>
    </p>
    <p class="mt-2 text-lg text-center">
      Dall'estero
      <a
        class="text-blue font-bold underline outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:rounded focus-visible:ring-blue"
        href="tel:+390284251701"
        >+39 02 84251701</a
      >
    </p>
  </div>
</template>

<script>
// Mixins
import FormMixin from '~/mixins/form'

// Components
import Accordion from '~/components/Accordion.vue'
import DropdownWithValidation from '~/components/DropdownWithValidation.vue'
import InputWithValidation from '~/components/InputWithValidation.vue'
import ModalInner from '~/components/ModalInner'
import ModalResponse from '~/components/ModalResponse'
import PrivacyCheckbox from '~/components/PrivacyCheckbox.vue'
import UcrsSwitch from '~/components/UcrsSwitch.vue'

export default {
  components: {
    Accordion,
    DropdownWithValidation,
    InputWithValidation,
    PrivacyCheckbox,
    UcrsSwitch,
  },

  mixins: [FormMixin],

  data() {
    return {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      message: '',
      privacy: false,
      hasBuyingInterest: 0,
      categories: [],
      typologies: [],
      subAdministrativeAreas: [],
      cities: [],
      category: null,
      typology: null,
      subAdministrativeArea: null,
      city: null,
      hasSellingInterest: 0,
      isDisabled: false,
    }
  },

  async mounted() {
    if (this.$store.state.auth.loggedIn) {
      this.first_name = this.$store.state.auth.user.first_name
      this.last_name = this.$store.state.auth.user.last_name
      this.email = this.$store.state.auth.user.email
      this.phone = this.$store.state.auth.user.phone
      this.privacy = true
    }
    const catRes = await this.$axios.get('/_api/api/v1/categories', {
      params: {
        sell_categories: true,
      },
    })
    this.categories = catRes.data.data
    const subAdministrativeAreasRes = await this.$axios.get(
      '/_api/api/v2/geo/states'
    )
    this.subAdministrativeAreas = subAdministrativeAreasRes.data.data
  },

  methods: {
    async onSelectCategory(selected) {
      this.typologies = []
      this.typology = ''
      this.category = selected
      if (!this.category) return
      const res = await this.$axios.get('/_api/api/v1/typologies', {
        params: {
          category: this.category.id,
        },
      })
      this.typologies = res.data.data

      this.tealiumFormUpdateLastField('category')
    },

    async onSelectState(selected) {
      this.cities = []
      this.city = ''
      this.subAdministrativeArea = selected
      if (!this.subAdministrativeArea) return
      const res = await this.$axios.get(
        `/_api/api/v2/geo/states/${this.subAdministrativeArea.id}`,
        {
          params: {
            embed: 'cities',
          },
        }
      )
      this.cities = res.data.data.cities

      this.tealiumFormUpdateLastField('state')
    },

    async validate() {
      await this.$refs.observer.validate()
      const fields = []
      if (this.$refs.observer.flags.invalid) {
        const errors = this.$refs.observer.errors
        for (const property in errors) {
          if (errors[property][0]) fields.push(property)
        }
      }
      return { isValid: this.$refs.observer.flags.valid, errorFields: fields }
    },

    async submit() {
      const validation = await this.validate()
      if (!validation.isValid) {
        const tealiumErrors = validation.errorFields.map((el) => {
          return { campo: el }
        })
        this.$tealium.track({
          event_name: 'errori_richiesta',
          errori_richiesta: tealiumErrors,
        })
        return
      }

      this.isDisabled = true

      try {
        const res = await this.$axios.post(`/_api/api/v1/leads`, {
          first_name: this.first_name,
          last_name: this.last_name,
          email: this.email,
          phone: this.phone,
          privacy: this.privacy ? 1 : 0,
          has_selling_interest: this.hasSellingInterest,
          has_buying_interest: this.hasBuyingInterest,
          type: 'generic',
          ...(this.message ? { message: this.message } : {}),
          ...(this.category ? { category: this.category.id } : {}),
          ...(this.typology ? { property_type: this.typology.id } : {}),
          ...(this.subAdministrativeArea
            ? { sub_administrative_area: this.subAdministrativeArea.id }
            : {}),
          ...(this.city ? { city: this.city.id } : {}),
        })

        this.$tealium.track({
          event_name: 'conferma_richiesta',
          user_email: this.email,
          user_type: '',
          id_getrix: res.data.data.lead_id,
          annuncio_interesse_vendo: this.hasSellingInterest ? 'SI' : 'NO',
        })

        const content = `<p class="font-medium text-3xl mb-12 text-green">La manifestazione di interesse è stata inviata con successo.</p><p class="text-xl mb-8 text-green">Un nostro operatore ti contatterà nei prossimi giorni da questo numero di telefono +39 02 8681 4901 - al seguente recapito: <strong>${this.phone}</strong>.<br aria-hidden="true"/>Se non riusciamo a trovarti libero, non preoccuparti. Ti invieremo un SMS con i nostri contatti e potrai richiamarci gratuitamente quando sarà più comodo per te.</p>`

        // TODO: Abstract component like auth.js (?)
        this.$oruga.modal.open({
          parent: this,
          width: '83rem',
          animation: 'ucrs-modal-',
          component: ModalInner,
          props: {
            bgColor: 'white',
            component: ModalResponse,
            componentProps: { icon: 'check', content },
          },
        })

        this.reset()
      } catch (error) {
        const content = `<p class="font-medium text-3xl">Errore, si prega di riprovare.</p>`

        this.$oruga.modal.open({
          parent: this,
          width: '83rem',
          animation: 'ucrs-modal-',
          component: ModalInner,
          props: {
            bgColor: 'white',
            component: ModalResponse,
            componentProps: { icon: 'danger', content },
          },
        })
      }

      this.isDisabled = false
    },

    reset() {
      this.typologies = []
      this.cities = []
      this.category = null
      this.typology = null
      this.subAdministrativeArea = null
      this.city = null
      if (!this.$store.state.auth.loggedIn) {
        this.first_name = ''
        this.last_name = ''
        this.email = ''
        this.phone = ''
        this.privacy = false
      }
      this.message = ''
      this.hasBuyingInterest = 0
      this.hasSellingInterest = 0
      this.isDisabled = false
      this.$refs.observer.reset()
    },
  },
}
</script>

<style lang="postcss">
@import '~/assets/css/components/field';
@import '~/assets/css/components/button';
@import '~/assets/css/components/input';
@import '~/assets/css/components/switch';
</style>
